import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedinIn, FaGithub, FaPaperPlane, FaCheckCircle, FaInstagram, FaShieldAlt, FaSync } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { SectionHeader, Card, Button, AnimatedSection, StaggerContainer, StaggerItem } from '../components/common';
import Input, { Textarea } from '../components/common/Input';
import { personalInfo } from '../data/profileData';

// =============================================================================
// SECURITY: Input validation constants (matching server-side)
// =============================================================================
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254;
const MAX_SUBJECT_LENGTH = 200;
const MAX_MESSAGE_LENGTH = 5000;
const MIN_MESSAGE_LENGTH = 10;

// Generate random numbers for math CAPTCHA
const generateCaptcha = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  return { num1, num2, answer: num1 + num2 };
};

// RFC 5322 compliant email validation
const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email) && email.length <= MAX_EMAIL_LENGTH;
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Math CAPTCHA state
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');

  // Rate limiting state (client-side UX improvement)
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const SUBMIT_COOLDOWN_MS = 5000; // 5 seconds between submissions

  // Refresh CAPTCHA
  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    setCaptchaInput('');
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Comprehensive client-side validation (matches server-side)
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Name validation
    const name = formData.name.trim();
    if (!name) {
      newErrors.name = 'Name is required';
    } else if (name.length > MAX_NAME_LENGTH) {
      newErrors.name = `Name must be less than ${MAX_NAME_LENGTH} characters`;
    }

    // Email validation
    const email = formData.email.trim();
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Subject validation
    const subject = formData.subject.trim();
    if (!subject) {
      newErrors.subject = 'Subject is required';
    } else if (subject.length > MAX_SUBJECT_LENGTH) {
      newErrors.subject = `Subject must be less than ${MAX_SUBJECT_LENGTH} characters`;
    }

    // Message validation
    const message = formData.message.trim();
    if (!message) {
      newErrors.message = 'Message is required';
    } else if (message.length < MIN_MESSAGE_LENGTH) {
      newErrors.message = `Message must be at least ${MIN_MESSAGE_LENGTH} characters`;
    } else if (message.length > MAX_MESSAGE_LENGTH) {
      newErrors.message = `Message must be less than ${MAX_MESSAGE_LENGTH} characters`;
    }

    return newErrors;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Client-side rate limiting (UX improvement)
    const now = Date.now();
    if (now - lastSubmitTime < SUBMIT_COOLDOWN_MS) {
      setErrors({ submit: 'Please wait a moment before submitting again.' });
      return;
    }

    const newErrors = validateForm();

    // Validate CAPTCHA
    const userAnswer = parseInt(captchaInput, 10);
    if (!captchaInput.trim()) {
      newErrors.captcha = 'Please solve the math problem';
    } else if (isNaN(userAnswer)) {
      newErrors.captcha = 'Please enter a valid number';
      refreshCaptcha();
    } else if (userAnswer !== captcha.answer) {
      newErrors.captcha = 'Incorrect answer. Please try again.';
      refreshCaptcha();
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Send contact form to PHP API
      const response = await fetch('/api/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: formData.subject.trim(),
          message: formData.message.trim(),
          // Honeypot field for bot detection (should be empty)
          website: '',
        }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server error. Please try again later or email directly.');
      }

      const result = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 429) {
          throw new Error('Too many requests. Please try again in a minute.');
        } else if (response.status === 400 && result.error === 'CAPTCHA verification failed') {
          refreshCaptcha();
          throw new Error('Security verification failed. Please try again.');
        } else if (result.details && Array.isArray(result.details)) {
          throw new Error(result.details.join(', '));
        }
        throw new Error(result.error || 'Failed to send message');
      }

      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setCaptchaInput('');
      setLastSubmitTime(now);
      refreshCaptcha();

      // Reset success message after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      // Don't log detailed errors to console in production
      setErrors({ submit: error.message || 'Failed to send message. Please try again or email directly.' });
      refreshCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, captcha, captchaInput, validateForm, refreshCaptcha, lastSubmitTime]);

  const contactInfo = [
    {
      icon: FaEnvelope,
      label: 'Email',
      value: personalInfo.email,
      href: `mailto:${personalInfo.email}`,
    },
    {
      icon: FaPhone,
      label: 'Phone',
      value: personalInfo.phone,
      href: `tel:${personalInfo.phone}`,
    },
    {
      icon: FaMapMarkerAlt,
      label: 'Location',
      value: personalInfo.location,
      href: null,
    },
  ];

  const socialLinks = [
    {
      icon: FaLinkedinIn,
      label: 'LinkedIn',
      href: personalInfo.linkedin,
      color: 'hover:bg-blue-600',
    },
    {
      icon: FaGithub,
      label: 'GitHub',
      href: personalInfo.github,
      color: 'hover:bg-gray-800',
    },
    {
      icon: FaXTwitter,
      label: 'X (Twitter)',
      href: 'https://x.com/rakeshkuma8912',
      color: 'hover:bg-black',
    },
    {
      icon: FaInstagram,
      label: 'Instagram',
      href: 'https://www.instagram.com/tripathirakeshkumar',
      color: 'hover:bg-pink-600',
    },
    {
      icon: FaEnvelope,
      label: 'Email',
      href: `mailto:${personalInfo.email}`,
      color: 'hover:bg-red-500',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Contact | Rakesh Kumar Tripathi</title>
        <meta name="description" content={`Get in touch with ${personalInfo.name} for IT consulting, ERPNext implementation, training, and technology solutions.`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-8 pb-8 gradient-bg-subtle">
        <div className="container-custom">
          <AnimatedSection>
            <SectionHeader
              badge="Get In Touch"
              title="Contact Me"
              subtitle="Have a project in mind or want to discuss technology solutions? I'd love to hear from you."
            />
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <AnimatedSection animation="fadeRight">
                <h2 className="text-2xl font-bold text-dark-800 mb-6">Let's Connect</h2>
                <p className="text-dark-500 mb-8">
                  IT consulting, ERPNext solutions, cybersecurity, cloud server installation (Azure/AWS/Utho), and website development—everything you need to build, secure, and scale your digital presence. Feel free to reach out through
                  any of the channels below.
                </p>

                {/* Contact Details */}
                <div className="space-y-4 mb-8">
                  {contactInfo.map((item) => (
                    <motion.div
                      key={item.label}
                      className="flex items-center space-x-4"
                      whileHover={{ x: 5 }}
                    >
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-primary-500" />
                      </div>
                      <div>
                        <p className="text-sm text-dark-400">{item.label}</p>
                        {item.href ? (
                          <a
                            href={item.href}
                            className="text-dark-800 font-medium hover:text-primary-500 transition-colors"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-dark-800 font-medium">{item.value}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Social Links */}
                <div>
                  <p className="text-sm text-dark-400 mb-4">Follow me on</p>
                  <div className="flex space-x-3">
                    {socialLinks.map((social) => (
                      <motion.a
                        key={social.label}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-dark-500 ${social.color} hover:text-white transition-all duration-300`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label={social.label}
                      >
                        <social.icon className="w-5 h-5" />
                      </motion.a>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <AnimatedSection animation="fadeLeft" delay={0.2}>
                <Card padding="lg" className="shadow-medium">
                  {isSubmitted ? (
                    <motion.div
                      className="text-center py-12"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <div className="w-20 h-20 mx-auto mb-6 bg-secondary-100 rounded-full flex items-center justify-center">
                        <FaCheckCircle className="w-10 h-10 text-secondary-500" />
                      </div>
                      <h3 className="text-2xl font-bold text-dark-800 mb-2">Message Sent!</h3>
                      <p className="text-dark-500">
                        Thank you for reaching out. I'll get back to you as soon as possible.
                      </p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} noValidate>
                      <h3 className="text-xl font-bold text-dark-800 mb-6">Send a Message</h3>

                      <div className="grid md:grid-cols-2 gap-4">
                        <Input
                          label="Your Name"
                          name="name"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleChange}
                          error={errors.name}
                          maxLength={MAX_NAME_LENGTH}
                          required
                        />
                        <Input
                          label="Email Address"
                          name="email"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          error={errors.email}
                          maxLength={MAX_EMAIL_LENGTH}
                          required
                        />
                      </div>

                      <Input
                        label="Subject"
                        name="subject"
                        placeholder="What's this about?"
                        value={formData.subject}
                        onChange={handleChange}
                        error={errors.subject}
                        maxLength={MAX_SUBJECT_LENGTH}
                        required
                      />

                      <Textarea
                        label="Message"
                        name="message"
                        placeholder="Tell me about your project or inquiry..."
                        value={formData.message}
                        onChange={handleChange}
                        error={errors.message}
                        rows={5}
                        maxLength={MAX_MESSAGE_LENGTH}
                        required
                      />
                      <p className="text-xs text-dark-400 -mt-2 mb-4">
                        {formData.message.length}/{MAX_MESSAGE_LENGTH} characters
                      </p>

                      {/* Math CAPTCHA */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-dark-700 mb-2">
                          <FaShieldAlt className="inline w-3 h-3 mr-2 text-green-500" />
                          Security Check
                        </label>
                        <div className="flex items-center space-x-3">
                          <div className="bg-gradient-to-r from-primary-100 to-secondary-100 px-4 py-3 rounded-lg select-none">
                            <span className="text-lg font-bold text-dark-800">
                              {captcha.num1} + {captcha.num2} = ?
                            </span>
                          </div>
                          <input
                            type="number"
                            value={captchaInput}
                            onChange={(e) => {
                              setCaptchaInput(e.target.value);
                              if (errors.captcha) {
                                setErrors((prev) => ({ ...prev, captcha: '' }));
                              }
                            }}
                            placeholder="?"
                            min="2"
                            max="20"
                            className={`w-20 px-3 py-3 border rounded-lg text-center font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                              errors.captcha ? 'border-red-500' : 'border-gray-300'
                            }`}
                            aria-label="CAPTCHA answer"
                          />
                          <button
                            type="button"
                            onClick={refreshCaptcha}
                            className="p-3 text-dark-400 hover:text-primary-500 transition-colors"
                            title="Get new question"
                            aria-label="Refresh CAPTCHA"
                          >
                            <FaSync className="w-4 h-4" />
                          </button>
                        </div>
                        {errors.captcha && (
                          <p className="mt-1 text-sm text-red-500">{errors.captcha}</p>
                        )}
                      </div>

                      {/* Submit Error */}
                      {errors.submit && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-600 text-sm">{errors.submit}</p>
                        </div>
                      )}

                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        fullWidth
                        loading={isSubmitting}
                        icon={<FaPaperPlane />}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  )}
                </Card>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Services CTA Section */}
      <section className="section-padding gradient-bg-subtle">
        <div className="container-custom">
          <SectionHeader
            badge="Services"
            title="How I Can Help"
            subtitle="Explore the range of IT services I offer"
          />

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'IT Consulting',
                description: 'Strategic technology consulting to help your business leverage IT for growth.',
              },
              {
                title: 'ERPNext Implementation',
                description: 'Complete ERPNext setup, customization, and support for your organization.',
              },
              {
                title: 'Cybersecurity',
                description: 'Security assessments, implementation, and training to protect your assets.',
              },
              {
                title: 'Corporate Training',
                description: 'Technical training programs on Linux, Python, cybersecurity, and more.',
              },
              {
                title: 'Cloud Solutions',
                description: 'AWS and Azure cloud setup, migration, and management services.',
              },
              {
                title: 'LMS Implementation',
                description: 'Moodle setup and customization for e-learning initiatives.',
              },
            ].map((service, index) => (
              <StaggerItem key={service.title}>
                <Card hover className="h-full">
                  <h3 className="text-lg font-bold text-dark-800 mb-2">{service.title}</h3>
                  <p className="text-dark-500 text-sm">{service.description}</p>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="h-64 md:h-96 bg-gray-200 relative">
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary-100 to-secondary-100">
          <div className="text-center">
            <FaMapMarkerAlt className="w-12 h-12 text-primary-500 mx-auto mb-4" />
            <p className="text-dark-600 font-medium">{personalInfo.location}</p>
            <p className="text-dark-400 text-sm">Available for remote consultations worldwide</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-dark-800">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-8">
              Let's discuss how my 25+ years of IT experience can help transform your business.
            </p>
            <Button
              variant="primary"
              size="lg"
              href={`mailto:${personalInfo.email}`}
              icon={<FaEnvelope />}
            >
              Email Me Directly
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Contact;
